import type { HttpClientConfig, ApiErrorResponse } from './types';

export class HttpClient {
  private config: HttpClientConfig;
  /** In-flight refresh, shared so concurrent 401s trigger a single renewal. */
  private refreshing: Promise<boolean> | null = null;

  constructor(config: HttpClientConfig) {
    this.config = config;
  }

  private resolveBaseUrl = async (): Promise<string> => {
    const { baseUrl } = this.config;
    return typeof baseUrl === 'function' ? baseUrl() : baseUrl;
  };

  private tryRefresh = (): Promise<boolean> => {
    if (!this.config.refresh) {
      return Promise.resolve(false);
    }
    if (!this.refreshing) {
      this.refreshing = this.config
        .refresh()
        .catch(() => false)
        .finally(() => {
          this.refreshing = null;
        });
    }
    return this.refreshing;
  };

  request = async <T>(
    path: string,
    options: RequestInit = {},
    isRetry = false,
  ): Promise<T> => {
    const token = await this.config.getToken();
    const base = await this.resolveBaseUrl();
    const url = path.startsWith('http') ? path : `${base}${path}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      if (!isRetry && (await this.tryRefresh())) {
        return this.request<T>(path, options, true);
      }
      this.config.onUnauthorized?.();
      throw new ApiError('UNAUTHORIZED', 'Authentication required', 401);
    }

    if (!response.ok) {
      const body = (await response
        .json()
        .catch(() => null)) as ApiErrorResponse | null;
      const code = body?.error?.code ?? 'REQUEST_FAILED';
      const message =
        body?.error?.message ?? `Request failed with status ${response.status}`;
      throw new ApiError(code, message, response.status);
    }

    return response.json() as Promise<T>;
  };

  get = <T>(path: string, options?: RequestInit): Promise<T> =>
    this.request<T>(path, { ...options, method: 'GET' });

  post = <T>(path: string, body?: unknown, options?: RequestInit): Promise<T> =>
    this.request<T>(path, {
      ...options,
      method: 'POST',
      body: body != null ? JSON.stringify(body) : undefined,
    });

  patch = <T>(
    path: string,
    body?: unknown,
    options?: RequestInit,
  ): Promise<T> =>
    this.request<T>(path, {
      ...options,
      method: 'PATCH',
      body: body != null ? JSON.stringify(body) : undefined,
    });

  del = <T>(path: string, options?: RequestInit): Promise<T> =>
    this.request<T>(path, { ...options, method: 'DELETE' });
}

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}
